export type FieldType = 'text' | 'textarea' | 'number' | 'money' | 'date' | 'select' | 'checkbox' | 'tags';

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  options?: string[];
  lookup?: string;
  required?: boolean;
  hideInTable?: boolean;
  hideInForm?: boolean;
}

export interface RowFilter {
  id: string;
  label: string;
  match: (row: Record<string, unknown>) => boolean;
}

export interface RecordSchema {
  title: string;
  url: string;
  page: string;
  writeAction: string;
  idKey?: string;
  fields: FieldDef[];
  lookups?: { key: string; url: string; label: string }[];
  detailPath?: string;
  newPath?: string;
  defaultFilter?: string;
  filters?: RowFilter[];
}

export const CUSTOMERS: RecordSchema = {
  title: 'Billing entities',
  url: '/api/customers',
  page: 'engagements',
  writeAction: 'edit',
  lookups: [{ key: 'reportingCustomerId', url: '/api/reporting-customers', label: 'name' }],
  fields: [
    { key: 'reportingCustomerName', label: 'Reporting customer', type: 'text', hideInForm: true },
    { key: 'reportingCustomerId', label: 'Reporting customer', type: 'select', lookup: 'reportingCustomerId', required: true, hideInTable: true },
    { key: 'code', label: 'Code', type: 'text', required: true },
    { key: 'name', label: 'Display name', type: 'text', required: true },
    { key: 'legalName', label: 'Legal billing name', type: 'text', required: true },
    { key: 'postalAddress', label: 'Billing address', type: 'textarea', hideInTable: true },
    { key: 'billingCurrency', label: 'Billing currency', type: 'select', options: ['GBP', 'EUR', 'USD'] },
    { key: 'billingEmails', label: 'Accounts payable emails', type: 'tags' },
    { key: 'invoiceQueryEmails', label: 'Invoice query emails', type: 'tags', hideInTable: true },
    { key: 'paymentTermsType', label: 'Payment terms', type: 'select', options: ['days', 'end_of_month', 'day_of_following_month', 'custom'], hideInTable: true },
    { key: 'paymentTermsDays', label: 'Net days', type: 'number', hideInTable: true },
    { key: 'paymentTermsDay', label: 'Day of following month', type: 'number', hideInTable: true },
    { key: 'paymentTermsConfirmed', label: 'Terms confirmed', type: 'checkbox' },
    { key: 'paymentTermsNotes', label: 'Terms evidence / notes', type: 'textarea', hideInTable: true },
    { key: 'invoiceTiming', label: 'Preferred invoice timing', type: 'text', hideInTable: true },
    { key: 'billingInstructions', label: 'Billing instructions / exceptions', type: 'textarea', hideInTable: true },
    { key: 'svcRequired', label: 'SVC required', type: 'checkbox' },
    { key: 'vatTreatment', label: 'VAT treatment', type: 'select', options: ['standard', 'zero_rated', 'exempt', 'out_of_scope'] },
    { key: 'vatRateBps', label: 'VAT rate (basis points; 2000 = 20%)', type: 'number', hideInTable: true },
    { key: 'relationship', label: 'Relationship', type: 'select', options: ['introduced', 'prospect', 'proposing', 'quoting', 'customer', 'former'] },
    { key: 'contactName', label: 'Contact name', type: 'text', hideInTable: true },
    { key: 'crunchName', label: 'Name in Crunch', type: 'text' },
    { key: 'sharePointFolder', label: 'SharePoint folder (e.g. RWG)', type: 'text', hideInTable: true },
  ],
};

export const REPORTING_CUSTOMERS: RecordSchema = {
  title: 'Reporting customers',
  url: '/api/reporting-customers',
  page: 'engagements',
  writeAction: 'edit',
  fields: [
    { key: 'code', label: 'Code', type: 'text', required: true },
    { key: 'name', label: 'Name', type: 'text', required: true },
  ],
};

export const ENGAGEMENTS: RecordSchema = {
  title: 'Engagements',
  url: '/api/engagements',
  page: 'engagements',
  writeAction: 'edit',
  lookups: [{ key: 'customerId', url: '/api/customers', label: 'name' }],
  fields: [
    { key: 'customerName', label: 'Customer', type: 'text', hideInForm: true },
    { key: 'customerId', label: 'Customer', type: 'select', lookup: 'customerId', required: true, hideInTable: true },
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'status', label: 'Status', type: 'select', options: ['proposed', 'contracted', 'active', 'closed'] },
    { key: 'stage', label: 'Stage', type: 'select', options: ['lead', 'opportunity', 'proposal', 'quotation', 'contracted', 'closed'] },
  ],
};

export const PURCHASE_ORDERS: RecordSchema = {
  title: 'Customer orders',
  url: '/api/purchase-orders',
  page: 'purchase_orders',
  writeAction: 'edit',
  detailPath: '/purchase-orders',
  newPath: '/purchase-orders/new',
  defaultFilter: 'unspent',
  filters: [
    { id: 'unspent', label: 'Unspent', match: (row) => Number(row.remainingPence) > 0 },
    { id: 'all', label: 'All', match: () => true },
    { id: 'spent', label: 'Spent', match: (row) => Number.isFinite(Number(row.remainingPence)) && Number(row.remainingPence) <= 0 },
  ],
  lookups: [{ key: 'engagementId', url: '/api/engagements', label: 'name' }],
  fields: [
    { key: 'customerName', label: 'Customer', type: 'text', hideInForm: true },
    { key: 'engagementName', label: 'Engagement', type: 'text', hideInForm: true },
    { key: 'engagementId', label: 'Engagement', type: 'select', lookup: 'engagementId', required: true, hideInTable: true },
    { key: 'poNumber', label: 'PO number', type: 'text', required: true },
    { key: 'valuePence', label: 'Value', type: 'money', required: true },
    { key: 'invoicedNetPence', label: 'Invoiced net', type: 'money', hideInForm: true },
    { key: 'invoicedVatPence', label: 'Invoiced VAT', type: 'money', hideInForm: true },
    { key: 'remainingPence', label: 'Remaining', type: 'money', hideInForm: true },
    { key: 'currency', label: 'Currency', type: 'select', options: ['GBP', 'EUR', 'USD'] },
    { key: 'vatLabel', label: 'VAT in total', type: 'text', hideInForm: true },
    { key: 'issuedOn', label: 'Issued', type: 'date' },
    { key: 'expiresOn', label: 'Expires', type: 'date' },
    { key: 'billingModel', label: 'Billing', type: 'select', options: ['fixed_price', 'time_and_materials'] },
    { key: 'status', label: 'Status', type: 'select', options: ['draft', 'open', 'expired', 'exhausted', 'cancelled', 'void'] },
    { key: 'sourceFileName', label: 'SharePoint file', type: 'text', hideInForm: true },
  ],
};

export const SUPPLIER_PURCHASE_ORDERS: RecordSchema = {
  title: 'Purchase orders',
  url: '/api/purchase-orders?direction=supplier',
  page: 'purchase_orders',
  writeAction: 'edit',
  detailPath: '/supplier-purchase-orders',
  newPath: '/supplier-purchase-orders/new',
  lookups: [{ key: 'vendorId', url: '/api/vendors', label: 'name' }],
  fields: [
    { key: 'vendorName', label: 'Supplier', type: 'text', hideInForm: true },
    { key: 'vendorId', label: 'Supplier', type: 'select', lookup: 'vendorId', required: true, hideInTable: true },
    { key: 'poNumber', label: 'PO number', type: 'text' },
    { key: 'docKind', label: 'Kind', type: 'select', options: ['services', 'licence', 'support', 'commercial'] },
    { key: 'valuePence', label: 'Value', type: 'money', required: true },
    { key: 'currency', label: 'Currency', type: 'select', options: ['GBP', 'EUR', 'USD'] },
    { key: 'issuedOn', label: 'Issued', type: 'date' },
    { key: 'expiresOn', label: 'Expires', type: 'date' },
    { key: 'status', label: 'Status', type: 'select', options: ['draft', 'issued', 'open', 'expired', 'exhausted', 'cancelled', 'void'] },
    { key: 'sourceFileName', label: 'SharePoint file', type: 'text', hideInForm: true },
  ],
};

export const CHARGE_LINES: RecordSchema = {
  title: 'Charge lines',
  url: '/api/charge-lines',
  page: 'charge_lines',
  writeAction: 'edit',
  lookups: [
    { key: 'engagementId', url: '/api/engagements', label: 'name' },
    { key: 'poId', url: '/api/purchase-orders', label: 'poNumber' },
  ],
  fields: [
    { key: 'customerName', label: 'Customer', type: 'text', hideInForm: true },
    { key: 'engagementName', label: 'Engagement', type: 'text', hideInForm: true },
    { key: 'engagementId', label: 'Engagement', type: 'select', lookup: 'engagementId', required: true, hideInTable: true },
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'chargeModel', label: 'Model', type: 'select', options: ['recurring', 'project', 'product'] },
    { key: 'kind', label: 'Kind', type: 'select', options: ['hosting', 'support', 'tnm', 'milestone', 'licence', 'services', 'other'] },
    { key: 'currency', label: 'Currency', type: 'select', options: ['GBP', 'EUR', 'USD'], hideInTable: true },
    { key: 'hourlyPence', label: 'Hourly rate', type: 'money', hideInTable: true },
    { key: 'dailyPence', label: 'Daily rate', type: 'money', hideInTable: true },
    { key: 'poNumber', label: 'PO', type: 'text', hideInForm: true },
    { key: 'poId', label: 'Purchase order', type: 'select', lookup: 'poId', hideInTable: true },
    { key: 'billToName', label: 'Bill to', type: 'text', hideInTable: true },
    { key: 'billToEmail', label: 'Invoice email', type: 'text', hideInTable: true },
    { key: 'billToAddress', label: 'Bill to address', type: 'textarea', hideInTable: true },
    { key: 'recurrence', label: 'Recurrence', type: 'select', options: ['none', 'monthly', 'quarterly', 'annual'] },
    { key: 'renewalOn', label: 'Renewal', type: 'date', hideInTable: true },
    { key: 'qty', label: 'Qty', type: 'number', hideInTable: true },
    { key: 'unitPence', label: 'Unit price', type: 'money', hideInTable: true },
    { key: 'active', label: 'Active', type: 'checkbox' },
  ],
};

export const INVOICES: RecordSchema = {
  title: 'Invoices',
  url: '/api/invoices',
  page: 'invoices',
  writeAction: 'draft',
  detailPath: '/invoices',
  newPath: '/invoices/new',
  defaultFilter: 'drafts',
  filters: [
    { id: 'unallocated', label: 'Unallocated', match: (row) => row['unallocated'] === true },
    { id: 'drafts', label: 'Drafts', match: (row) => row['status'] === 'draft' },
    { id: 'unpaid', label: 'Issued', match: (row) => row['status'] === 'issued' },
    { id: 'paid', label: 'Paid', match: (row) => row['status'] === 'paid' },
    { id: 'invoiced', label: 'Invoiced', match: (row) => row['status'] === 'issued' || row['status'] === 'paid' },
    { id: 'credited', label: 'Credited', match: (row) => row['status'] === 'credited' || row['creditTag'] === 'credited' || row['creditTag'] === 'part_credited' || row['partCredited'] === true },
    { id: 'books', label: 'Books', match: (row) => Boolean(row['crunchInvoiceId']) },
    { id: 'all', label: 'All', match: () => true },
  ],
  lookups: [
    { key: 'customerId', url: '/api/customers', label: 'name' },
    { key: 'engagementId', url: '/api/engagements', label: 'name' },
    { key: 'chargeLineId', url: '/api/charge-lines', label: 'name' },
    { key: 'poId', url: '/api/purchase-orders', label: 'poNumber' },
  ],
  fields: [
    { key: 'number', label: 'Number', type: 'text' },
    { key: 'revision', label: 'Revision', type: 'number', hideInForm: true },
    { key: 'internalNumber', label: 'Internal name', type: 'text', hideInForm: true },
    { key: 'customerName', label: 'Customer', type: 'text', hideInForm: true },
    { key: 'poNumber', label: 'PO', type: 'text', hideInForm: true },
    { key: 'engagementName', label: 'Engagement', type: 'text', hideInForm: true },
    { key: 'chargeLineName', label: 'Charge line', type: 'text', hideInForm: true },
    { key: 'engagementId', label: 'Engagement', type: 'select', lookup: 'engagementId', hideInTable: true },
    { key: 'chargeLineId', label: 'Charge line', type: 'select', lookup: 'chargeLineId', hideInTable: true },
    { key: 'status', label: 'Status', type: 'select', options: ['draft', 'issued', 'void', 'paid', 'credited'] },
    { key: 'deliveryStatus', label: 'Email', type: 'text', hideInForm: true },
    { key: 'filingStatus', label: 'SharePoint', type: 'text', hideInForm: true },
    { key: 'issuedOn', label: 'Issued', type: 'date', hideInForm: true },
    { key: 'netPence', label: 'Net', type: 'money', hideInForm: true },
    { key: 'vatPence', label: 'VAT', type: 'money', hideInForm: true },
    { key: 'totalPence', label: 'Total', type: 'money', hideInForm: true },
    { key: 'amountPence', label: 'Amount', type: 'money', hideInTable: true },
    { key: 'currency', label: 'Currency', type: 'select', options: ['GBP', 'EUR', 'USD'] },
    { key: 'gbpNetPence', label: 'GBP net', type: 'money', hideInForm: true },
    { key: 'gbpAmountPence', label: 'GBP total', type: 'money', hideInForm: true },
    { key: 'periodStart', label: 'Period start', type: 'date' },
    { key: 'periodEnd', label: 'Period end', type: 'date' },
    { key: 'poId', label: 'Purchase order', type: 'select', lookup: 'poId', hideInTable: true },
  ],
};

export const INVOICE_TEMPLATES: RecordSchema = {
  title: 'Invoice templates',
  url: '/api/invoice-templates',
  page: 'invoices',
  writeAction: 'draft',
  detailPath: '/invoice-templates',
  newPath: '/invoice-templates/new',
  defaultFilter: 'all',
  filters: [
    { id: 'all', label: 'All', match: () => true },
    { id: 'active', label: 'Active', match: (row) => row.status === 'active' },
    { id: 'paused', label: 'Paused', match: (row) => row.status === 'paused' },
  ],
  fields: [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'customerName', label: 'Customer', type: 'text', hideInForm: true },
    { key: 'engagementName', label: 'Engagement', type: 'text', hideInForm: true },
    { key: 'chargeLineName', label: 'Charge line', type: 'text', hideInForm: true },
    { key: 'status', label: 'Status', type: 'select', options: ['active', 'paused'] },
    { key: 'recurrence', label: 'Schedule', type: 'select', options: ['off', 'monthly', 'quarterly', 'yearly'] },
    { key: 'nextOn', label: 'Next run', type: 'date' },
    { key: 'onRun', label: 'When it runs', type: 'select', options: ['draft', 'issue'] },
    { key: 'currency', label: 'Currency', type: 'select', options: ['GBP', 'EUR', 'USD'] },
    { key: 'netPence', label: 'Net', type: 'money', hideInForm: true },
    { key: 'vatPence', label: 'VAT', type: 'money', hideInForm: true },
    { key: 'totalPence', label: 'Total', type: 'money', hideInForm: true },
    { key: 'periodStart', label: 'Period start', type: 'date' },
    { key: 'periodEnd', label: 'Period end', type: 'date' },
    { key: 'lastRunOn', label: 'Last run', type: 'date', hideInForm: true },
    { key: 'lastError', label: 'Last result', type: 'text', hideInForm: true },
  ],
};

export const COMMERCIAL: RecordSchema = {
  title: 'Sales offers',
  url: '/api/commercial',
  page: 'quotations',
  writeAction: 'draft',
  newPath: '/quotations/new',
  fields: [
    { key: 'number', label: 'Number', type: 'text' },
    { key: 'revision', label: 'Revision', type: 'text' },
    { key: 'customerName', label: 'Customer', type: 'text' },
    { key: 'title', label: 'Title', type: 'text' },
    { key: 'status', label: 'Status', type: 'text' },
    { key: 'issuedOn', label: 'Issued', type: 'date', hideInTable: true },
  ],
};

export const QUOTATIONS: RecordSchema = {
  title: 'Quotations',
  url: '/api/quotations',
  page: 'quotations',
  writeAction: 'draft',
  detailPath: '/quotations',
  newPath: '/quotations/new',
  lookups: [
    { key: 'customerId', url: '/api/customers', label: 'name' },
    { key: 'engagementId', url: '/api/engagements', label: 'name' },
  ],
  fields: [
    { key: 'number', label: 'Number', type: 'text' },
    { key: 'customerName', label: 'Customer', type: 'text', hideInForm: true },
    { key: 'customerId', label: 'Customer', type: 'select', lookup: 'customerId', required: true, hideInTable: true },
    { key: 'title', label: 'Title', type: 'text', required: true },
    { key: 'status', label: 'Status', type: 'select', options: ['draft', 'issued', 'void'] },
    { key: 'amountPence', label: 'Amount', type: 'money', required: true },
    { key: 'currency', label: 'Currency', type: 'select', options: ['GBP', 'EUR', 'USD'] },
    { key: 'validDays', label: 'Valid days', type: 'number' },
    { key: 'issuedOn', label: 'Issued', type: 'date' },
    { key: 'purpose', label: 'Purpose', type: 'text', hideInTable: true },
    { key: 'engagementId', label: 'Engagement', type: 'select', lookup: 'engagementId', hideInTable: true },
  ],
};

export const VENDORS: RecordSchema = {
  title: 'Vendors',
  url: '/api/vendors',
  page: 'vendors',
  writeAction: 'edit',
  fields: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'contactName', label: 'Contact name', type: 'text' },
    { key: 'postalAddress', label: 'Postal address', type: 'textarea' },
    { key: 'dayHours', label: 'Day hours', type: 'number' },
    { key: 'emails', label: 'Emails', type: 'tags' },
  ],
};

export const BILLS: RecordSchema = {
  title: 'Bills',
  url: '/api/bills',
  page: 'bills',
  writeAction: 'ingest',
  detailPath: '/bills',
  lookups: [
    { key: 'vendorId', url: '/api/vendors', label: 'name' },
    { key: 'engagementId', url: '/api/engagements', label: 'name' },
    { key: 'poId', url: '/api/purchase-orders?direction=supplier', label: 'poNumber' },
  ],
  fields: [
    { key: 'vendorName', label: 'Vendor', type: 'text', hideInForm: true },
    { key: 'poNumber', label: 'Purchase order', type: 'text', hideInForm: true },
    { key: 'vendorId', label: 'Vendor', type: 'select', lookup: 'vendorId', hideInTable: true },
    { key: 'number', label: 'Number', type: 'text', required: true },
    { key: 'status', label: 'Status', type: 'select', options: ['inbox', 'draft', 'approved', 'scheduled', 'paid', 'on_hold', 'void', 'mismatch'] },
    { key: 'amountPence', label: 'Amount', type: 'money' },
    { key: 'expenseType', label: 'Category', type: 'text', hideInForm: true },
    { key: 'currency', label: 'Currency', type: 'select', options: ['GBP', 'EUR', 'USD'] },
    { key: 'gbpAmountPence', label: 'GBP', type: 'money', hideInForm: true },
    { key: 'qty', label: 'Qty', type: 'number' },
    { key: 'unit', label: 'Unit', type: 'select', options: ['hour', 'day'] },
    { key: 'periodStart', label: 'Period start', type: 'date', hideInTable: true },
    { key: 'periodEnd', label: 'Period end', type: 'date', hideInTable: true },
    { key: 'dueOn', label: 'Due', type: 'date' },
    { key: 'customerName', label: 'Customer', type: 'text', hideInForm: true },
    { key: 'engagementId', label: 'Engagement', type: 'select', lookup: 'engagementId', hideInTable: true },
    { key: 'poId', label: 'Purchase order', type: 'select', lookup: 'poId', hideInTable: true },
  ],
};

export const EXPENSES: RecordSchema = {
  title: 'Expenses',
  url: '/api/expenses',
  page: 'expenses',
  writeAction: 'submit',
  lookups: [{ key: 'engagementId', url: '/api/engagements', label: 'name' }],
  fields: [
    { key: 'claimantEmail', label: 'Claimant', type: 'text', required: true },
    { key: 'number', label: 'Number', type: 'text', required: true },
    { key: 'status', label: 'Status', type: 'select', options: ['inbox', 'draft', 'approved', 'scheduled', 'paid', 'on_hold', 'void'] },
    { key: 'amountPence', label: 'Amount', type: 'money' },
    { key: 'expenseType', label: 'Category', type: 'text', hideInForm: true },
    { key: 'currency', label: 'Currency', type: 'select', options: ['GBP', 'EUR', 'USD'] },
    { key: 'dueOn', label: 'Due', type: 'date' },
    { key: 'customerName', label: 'Customer', type: 'text', hideInForm: true },
    { key: 'engagementId', label: 'Engagement', type: 'select', lookup: 'engagementId', hideInTable: true },
    { key: 'preApproved', label: 'Pre-approved', type: 'checkbox' },
  ],
};

export const PEOPLE: RecordSchema = {
  title: 'People',
  url: '/api/people',
  page: 'people',
  writeAction: 'edit',
  lookups: [{ key: 'vendorId', url: '/api/vendors', label: 'name' }],
  fields: [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'email', label: 'Email', type: 'text', required: true },
    { key: 'kind', label: 'Kind', type: 'select', options: ['employee', 'contractor'] },
    { key: 'vendorName', label: 'Vendor', type: 'text', hideInForm: true },
    { key: 'vendorId', label: 'Vendor', type: 'select', lookup: 'vendorId', hideInTable: true },
    { key: 'dayHours', label: 'Day hours', type: 'number' },
    { key: 'clockifyUserId', label: 'Clockify user ID', type: 'text', hideInTable: true },
    { key: 'active', label: 'Active', type: 'checkbox' },
  ],
};

export const PAYMENTS_IN: RecordSchema = {
  title: 'Payments in',
  url: '/api/payments-in',
  page: 'payments',
  writeAction: 'allocate',
  lookups: [{ key: 'invoiceId', url: '/api/invoices', label: 'number' }],
  fields: [
    { key: 'invoiceNumber', label: 'Invoice', type: 'text', hideInForm: true },
    { key: 'customerName', label: 'Customer', type: 'text', hideInForm: true },
    { key: 'invoiceId', label: 'Invoice', type: 'select', lookup: 'invoiceId', hideInTable: true },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'amountPence', label: 'Amount', type: 'money', required: true },
    { key: 'paidOn', label: 'Paid on', type: 'date' },
  ],
};

export const PAYMENTS_OUT: RecordSchema = {
  title: 'Payments out',
  url: '/api/payments-out',
  page: 'payments',
  writeAction: 'allocate',
  lookups: [{ key: 'billId', url: '/api/bills', label: 'number' }],
  fields: [
    { key: 'billNumber', label: 'Bill', type: 'text', hideInForm: true },
    { key: 'poNumber', label: 'Purchase order', type: 'text', hideInForm: true },
    { key: 'vendorName', label: 'Vendor', type: 'text', hideInForm: true },
    { key: 'billId', label: 'Bill', type: 'select', lookup: 'billId', required: true, hideInTable: true },
    { key: 'amountPence', label: 'Amount', type: 'money', required: true },
    { key: 'paidOn', label: 'Paid on', type: 'date' },
  ],
};
